import { useEffect, useRef } from 'react';
import {
  PLAY_ALONG_ACTIVITY_EVENT,
  PLAY_ALONG_IDLE_PAUSE_MS,
  makeIdleTimer,
} from 'Utils/playAlong/costGuards';

// Anything the player does on the page counts: a click or tap, a key, the wheel
// or a scroll (tempo changes are clicks, taps or keys). A note played on a
// connected keyboard counts too, through notePlayAlongActivity.
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

export interface PlayAlongIdlePauseOptions {
  /** True while ungraded play is running. */
  active: boolean;
  /** Called once when the time passes with no interaction. */
  onIdle: () => void;
  timeoutMs?: number;
}

/**
 * While `active`, calls `onIdle` after `timeoutMs` with no interaction. Each
 * interaction restarts the countdown. Stops when `active` turns false.
 */
export function usePlayAlongIdlePause({
  active,
  onIdle,
  timeoutMs = PLAY_ALONG_IDLE_PAUSE_MS,
}: PlayAlongIdlePauseOptions): void {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!active) return undefined;
    const timer = makeIdleTimer({ timeoutMs, onIdle: () => onIdleRef.current() });
    timer.arm();
    INTERACTION_EVENTS.forEach((type) =>
      window.addEventListener(type, timer.touch, { capture: true, passive: true }),
    );
    window.addEventListener(PLAY_ALONG_ACTIVITY_EVENT, timer.touch);
    return () => {
      timer.disarm();
      INTERACTION_EVENTS.forEach((type) =>
        window.removeEventListener(type, timer.touch, { capture: true }),
      );
      window.removeEventListener(PLAY_ALONG_ACTIVITY_EVENT, timer.touch);
    };
  }, [active, timeoutMs]);
}
